import React, { useEffect, useRef, useState, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import FormApi from "../../../Api/FormApi"
import {
  Button,
  Col,
  Row,
  Card,
  CardBody,
  Badge,
  CardHeader,
  CardTitle,
  Table,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { Toast } from "primereact/toast"

const ViewResponse = () => {
  const { responseId } = useParams()
  document.title = "Response Details"
  const navigate = useNavigate()
  const toast = useRef(null)
  const accessToken = localStorage.getItem("accessToken")

  const [response, setResponse] = useState(null)
  const [loading, setLoading] = useState(true)

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Forms", link: "/forms" },
    { label: "Submitted Forms", link: "/all-responses" },
    { label: "Response Details" },
  ]

  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const getResponseDetails = useCallback(() => {
    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    FormApi.getResponseById(responseId, config)
      .then(res => {
        setLoading(false)
        if (res.data.status) {
          setResponse(res.data.response)
        }
      })
      .catch(error => {
        console.error("Error fetching response details:", error)
        setLoading(false)
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to fetch response details"
        })
      })
  }, [accessToken, responseId])

  useEffect(() => {
    if (responseId) {
      getResponseDetails()
    }
  }, [responseId, getResponseDetails])

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading response details...</p>
        </div>
      </div>
    )
  }

  if (!response) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="text-center mt-5">
            <h3>Response not found</h3>
            <Button color="primary" onClick={() => navigate("/all-responses")}>
              Back to List
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Toast ref={toast} />
      <div className="page-content">
        <div className="container-fluid">
          <CustomBreadCrumb
            data={breadcrumbItems}
            pageTitle="Response Details"
            handleClick={handleBreadcrumbClick}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex align-items-center justify-content-between">
                <Button 
                  color="light" 
                  onClick={() => navigate("/all-responses")}
                  className="btn-sm"
                >
                  <i className="mdi mdi-arrow-left me-1"></i> Back to Responses
                </Button>
                <div>
                   <Badge 
                    color={
                      response.status === "submitted" ? "success" : 
                      response.status === "spam" ? "danger" : "secondary"
                    }
                    className="p-2 fs-6"
                  >
                    {response.status.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={4}>
              <Card>
                <CardHeader>
                  <CardTitle className="mb-0">Respondent Information</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <p className="text-muted mb-1">Name</p>
                    <h6 className="mb-0">{response.respondentName || "Anonymous"}</h6>
                  </div>
                  <div className="mb-3">
                    <p className="text-muted mb-1">Email</p>
                    <h6 className="mb-0">{response.respondentEmail || "N/A"}</h6>
                  </div>
                  <div className="mb-3">
                    <p className="text-muted mb-1">Submitted On</p>
                    <h6 className="mb-0">{new Date(response.createdAt).toLocaleString()}</h6>
                  </div>
                  <div className="mb-0">
                    <p className="text-muted mb-1">Form Name</p>
                    <h6 className="mb-0 text-primary">{response.formId?.formName || "Unknown Form"}</h6>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={8}>
              <Card>
                <CardHeader>
                  <CardTitle className="mb-0">Form Responses</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="table-responsive">
                    <Table borderless className="table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "30%" }}>Field Name</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {response.responses && response.responses.length > 0 ? (
                          response.responses.map((field, idx) => (
                            <tr key={idx} className="border-bottom">
                              <td>
                                <span className="fw-medium text-dark">{field.fieldName}</span>
                              </td>
                              <td className="text-wrap">
                                {typeof field.value === "boolean" 
                                  ? (field.value ? "Yes" : "No") 
                                  : (field.value || "-")}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" className="text-center">No field data available</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
              
              {response.meta && Object.keys(response.meta).length > 0 && (
                <Card className="mt-3">
                  <CardHeader>
                    <CardTitle className="mb-0">Additional Metadata</CardTitle>
                  </CardHeader>
                  <CardBody>
                     <pre className="bg-light p-3 rounded">
                        {JSON.stringify(response.meta, null, 2)}
                     </pre>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </>
  )
}

export default ViewResponse
